/**
 * <s-signup-popup> — shows a sign-up dialog once per visitor.
 *
 * Opens the child <dialog> after `delay` seconds or on desktop exit intent
 * (whichever comes first), then remembers it in localStorage so it never
 * shows again in that browser. After the visitor submits the form inside it,
 * the page reloads with the form's feedback, so the dialog reopens once to
 * show the success or error message.
 *
 * Attributes:
 *   delay        — seconds before opening (default 10)
 *   exit-intent  — also open when the pointer leaves through the top of the window
 */
const SEEN_KEY = 'cosmos-signup-popup-seen';
const SUBMITTED_KEY = 'cosmos-signup-popup-submitted';

class SSignupPopup extends HTMLElement {
  #ac;
  #triggerAc;
  #timer = null;
  #dialog;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    if (!this.#dialog) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.querySelector('form')?.addEventListener('submit', () => {
      this.#store(sessionStorage, SUBMITTED_KEY, '1');
    }, opts);

    if (this.#read(sessionStorage, SUBMITTED_KEY)) {
      sessionStorage.removeItem(SUBMITTED_KEY);
      this.#open(true);
      return;
    }

    if (this.#read(localStorage, SEEN_KEY)) return;

    const delay = Number.parseFloat(this.getAttribute('delay') ?? '10');
    this.#timer = window.setTimeout(() => this.#open(), (Number.isFinite(delay) ? delay : 10) * 1000);

    this.#triggerAc = new AbortController();
    if (this.hasAttribute('exit-intent') && window.matchMedia('(pointer: fine)').matches) {
      document.documentElement.addEventListener('mouseleave', (event) => {
        if (event.clientY <= 0) this.#open();
      }, { signal: this.#triggerAc.signal });
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#triggerAc?.abort();
    window.clearTimeout(this.#timer);
  }

  #open(force = false) {
    if (this.#dialog.open) return;
    // Never interrupt another open panel such as the bag or search; try again shortly.
    if (!force && document.querySelector('dialog[open]')) {
      window.clearTimeout(this.#timer);
      this.#timer = window.setTimeout(() => this.#open(), 3000);
      return;
    }
    window.clearTimeout(this.#timer);
    this.#triggerAc?.abort();
    this.#store(localStorage, SEEN_KEY, '1');
    this.#dialog.showModal();
  }

  #read(storage, key) {
    try {
      return storage.getItem(key);
    } catch {
      return null;
    }
  }

  #store(storage, key, value) {
    try {
      storage.setItem(key, value);
    } catch {
      // Storage can be unavailable in private modes; the pop-up then simply shows per page load.
    }
  }
}

if (!customElements.get('s-signup-popup')) customElements.define('s-signup-popup', SSignupPopup);
